<template>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <!-- Users -->
        <div class="rounded-xl border border-gray-200 bg-white p-5">
            <p
                class="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-400"
            >
                {{ t("vue.kiosk_da_kpi_users") }}
            </p>
            <div class="space-y-3">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500"
                        >
                            <UserPlusIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_new_registrations")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.new_users
                    }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-green-500"
                        >
                            <CheckBadgeIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_email_verifications")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.verified_emails
                    }}</span>
                </div>
                <div
                    class="flex items-center justify-between border-t border-gray-100 pt-2"
                >
                    <span class="text-xs text-gray-400">{{
                        t("vue.kiosk_da_verification_ratio")
                    }}</span>
                    <span
                        class="text-sm font-semibold"
                        :class="
                            summary.new_users > 0
                                ? 'text-gray-700'
                                : 'text-gray-400'
                        "
                    >
                        {{ verificationRatio }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Teams -->
        <div class="rounded-xl border border-gray-200 bg-white p-5">
            <p
                class="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-400"
            >
                {{ t("vue.kiosk_da_kpi_teams") }}
            </p>
            <div class="space-y-3">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-500"
                        >
                            <UserGroupIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_new_teams")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.new_teams
                    }}</span>
                </div>
                <div
                    class="flex items-center justify-between pl-9 text-xs text-gray-400"
                >
                    <span>{{ t("vue.kiosk_da_new_registration_teams") }}</span>
                    <span class="font-semibold text-gray-600">{{
                        summary.new_registration_teams
                    }}</span>
                </div>
                <div
                    class="flex items-center justify-between border-b border-gray-100 pb-2 pl-9 text-xs text-gray-400"
                >
                    <span>{{ t("vue.kiosk_da_new_standalone_teams") }}</span>
                    <span class="font-semibold text-gray-600">{{
                        summary.new_standalone_teams
                    }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-500"
                        >
                            <RocketLaunchIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_team_updates")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.team_updates
                    }}</span>
                </div>
            </div>
        </div>

        <!-- Plans -->
        <div class="rounded-xl border border-gray-200 bg-white p-5">
            <p
                class="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-400"
            >
                {{ t("vue.kiosk_da_kpi_plans") }}
            </p>
            <div class="space-y-2.5">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500"
                        >
                            <CreditCardIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_new_free_plans")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.new_free_plans
                    }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500"
                        >
                            <ArrowPathIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_free_plan_restarts")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.free_plan_restarts
                    }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-600"
                        >
                            <CurrencyDollarIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_new_subscriptions")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.new_subscriptions
                    }}</span>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <div
                            class="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500"
                        >
                            <ClockIcon class="h-4 w-4 text-white" />
                        </div>
                        <span class="text-sm text-gray-600">{{
                            t("vue.kiosk_da_ending_trials")
                        }}</span>
                    </div>
                    <span class="text-xl font-bold text-gray-900">{{
                        summary.ending_trials
                    }}</span>
                </div>
                <div
                    class="flex items-center justify-between pl-9 text-xs text-gray-400"
                >
                    <span>{{ t("vue.kiosk_da_trial_expired") }}</span>
                    <span class="font-semibold text-gray-600">{{
                        summary.expired_trials
                    }}</span>
                </div>
                <div
                    class="flex items-center justify-between pl-9 text-xs text-gray-400"
                >
                    <span>{{ t("vue.kiosk_da_trial_converted") }}</span>
                    <span class="font-semibold text-emerald-600">{{
                        summary.trial_conversions
                    }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import {
    UserPlusIcon,
    CheckBadgeIcon,
    UserGroupIcon,
    CreditCardIcon,
    RocketLaunchIcon,
    ClockIcon,
    ArrowPathIcon,
    CurrencyDollarIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { ISummary } from "../types";

const { t } = useTranslations();

const props = defineProps<{
    summary: ISummary;
}>();

const verificationRatio = computed((): string => {
    if (props.summary.new_users === 0) return "—";
    const ratio = Math.round(
        (props.summary.verified_emails / props.summary.new_users) * 100,
    );
    return `${ratio} %`;
});
</script>
